import type { ReviewDetail } from '../pages/dispatching/preplan-v2/reviewModel';
import api from './api';
import {
  GeneratePrePlanRequest,
  GeneratePrePlanResponse,
  GetPrePlansResponse,
  PrePlanConfig,
  PrePlanWorkScope,
  FullConfigResponse
} from '../types/preplanning';
import type { StrategyInterpretation } from '../pages/dispatching/preplan-v2/betaStrategyModel';

export interface RegionOption {
  name: string;
}

/** Which configuration area to read/write. The smart_dispatch_config table
 *  stores separate rows per area, so callers MUST specify which area they want. */
export type ConfigArea = 'preplanning' | 'dispatch';

const preplanningService = {
  getWorkScope: async (request: Pick<GeneratePrePlanRequest, 'target_date' | 'region_name' | 'preference_filter'>): Promise<PrePlanWorkScope> => {
    const params = new URLSearchParams({ target_date: request.target_date, region_name: request.region_name,
      preference_filter: request.preference_filter ?? 'All' });
    const response = await api.get(`/smart-dispatch/preplan-work-scope?${params.toString()}`);
    return response.data;
  },
  /**
   * Generate a new pre-plan for a target date and region
   */
  generatePrePlan: async (request: GeneratePrePlanRequest): Promise<GeneratePrePlanResponse> => {
    const response = await api.post('/smart-dispatch/generate-preplan', request);
    return response.data;
  },

  /** Beta "Describe your day": record the free-text ask and get the AI's mapping
   *  onto a preset plus extras. The row is written even when the AI fails. */
  interpretStrategy: async (request: {
    request_text: string; region_name: string; target_date: string;
    preference_filter?: string; include_assigned_moves?: boolean;
  }): Promise<StrategyInterpretation> => {
    const response = await api.post('/smart-dispatch/strategy-requests', request);
    return response.data;
  },

  /** Close out a free-text request with what was actually generated. */
  recordStrategyOutcome: async (requestId: number, outcome: {
    final_strategy: string; final_priorities: string[]; plan_id?: string;
  }): Promise<void> => {
    await api.patch(`/smart-dispatch/strategy-requests/${requestId}`, outcome);
  },

  /**
   * Get saved pre-plans for a specific date
   */
  getPrePlans: async (
    targetDate: string,
    regionName?: string,
    status?: string
  ): Promise<GetPrePlansResponse> => {
    const params = new URLSearchParams();
    params.append('target_date', targetDate);
    if (regionName) params.append('region_name', regionName);
    if (status) params.append('status', status);

    const response = await api.get(`/smart-dispatch/preplans?${params.toString()}`);
    return response.data;
  },

  /**
   * Get a specific pre-plan by ID
   */
  getPrePlan: async (planId: string): Promise<{
    plan: Record<string, unknown>;
    assignments: Record<string, unknown>[];
    unassigned: Record<string, unknown>[];
  }> => {
    const response = await api.get(`/smart-dispatch/preplans/${planId}`);
    return response.data;
  },

  /** Saved evidence only: this never generates or applies a plan. */
  getPrePlanReview: async (planId: string): Promise<ReviewDetail> => {
    const response = await api.get(`/smart-dispatch/preplans/${encodeURIComponent(planId)}?include_execution=true`);
    return response.data;
  },

  /**
   * Update the status of a pre-plan
   */
  updatePrePlanStatus: async (
    planId: string,
    status: string
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.patch(`/smart-dispatch/preplans/${planId}/status`, {
      status,
    });
    return response.data;
  },

  /**
   * Get available regions for pre-planning
   */
  getRegions: async (): Promise<RegionOption[]> => {
    // Use the existing dispatchers endpoint to get revenue regions
    const response = await api.get('/orders/regions');
    const data = response.data?.data;

    if (data?.revenueRegions) {
      return data.revenueRegions.map((name: string) => ({
        name,
      }));
    }

    return [];
  },

  /**
   * Get smart dispatch configuration for the given area
   */
  getConfig: async (area: ConfigArea = 'preplanning'): Promise<PrePlanConfig> => {
    const response = await api.get(`/smart-dispatch/config?area=${area}`);
    return response.data;
  },

  /**
   * Update multiple config values at once (weights) for the given area
   */
  updateWeights: async (weights: {
    distance: number;
    workload: number;
    profitability: number;
    driver_score: number;
  }, region?: string, area: ConfigArea = 'preplanning'): Promise<{ success: boolean }> => {
    // Update each weight individually
    await Promise.all([
      api.put('/smart-dispatch/config/weight_distance', {
        value: weights.distance.toFixed(2),
        region,
        area,
      }),
      api.put('/smart-dispatch/config/weight_workload', {
        value: weights.workload.toFixed(2),
        region,
        area,
      }),
      api.put('/smart-dispatch/config/weight_profitability', {
        value: weights.profitability.toFixed(2),
        region,
        area,
      }),
      api.put('/smart-dispatch/config/weight_driver_score', {
        value: weights.driver_score.toFixed(2),
        region,
        area,
      }),
    ]);

    return { success: true };
  },

  /**
   * Get full config with all values and region overrides for the given area
   */
  getFullConfig: async (
    region?: string,
    area: ConfigArea = 'preplanning'
  ): Promise<FullConfigResponse> => {
    const params = new URLSearchParams({ area });
    if (region) params.append('region', region);
    const response = await api.get(`/smart-dispatch/config?${params.toString()}`);
    return response.data;
  },

  /**
   * Update a single config value within the given area
   */
  updateConfig: async (
    key: string,
    value: string,
    region?: string,
    area: ConfigArea = 'preplanning'
  ): Promise<{ success: boolean }> => {
    await api.put(`/smart-dispatch/config/${key}`, {
      value,
      region,
      area,
    });
    return { success: true };
  },

  /**
   * Delete a region-specific config override within the given area
   * This reverts the config to use the global default for that region+area
   */
  deleteConfigOverride: async (
    key: string,
    region: string,
    area: ConfigArea = 'preplanning'
  ): Promise<{ success: boolean }> => {
    const params = new URLSearchParams({ region, area });
    await api.delete(`/smart-dispatch/config/${key}?${params.toString()}`);
    return { success: true };
  },

  /**
   * Get effective config values for a specific region+area
   * Merges global defaults with region-specific overrides
   */
  getEffectiveConfig: async (
    region?: string,
    area: ConfigArea = 'preplanning'
  ): Promise<PrePlanConfig> => {
    const params = new URLSearchParams({ area });
    if (region) params.append('region', region);
    const response = await api.get(`/smart-dispatch/config?${params.toString()}`);
    const configData = response.data.config;

    // Helper to get effective value (region override or global default)
    const getValue = (key: string, defaultValue: string): string => {
      const item = configData[key];
      if (!item) return defaultValue;

      // Check for region-specific override
      if (region && item.overrides) {
        const override = item.overrides.find((o: { region: string }) => o.region === region);
        if (override) return override.value;
      }

      return item.value || defaultValue;
    };

    return {
      weight_distance: parseFloat(getValue('weight_distance', '0.30')),
      weight_workload: parseFloat(getValue('weight_workload', '0.25')),
      weight_profitability: parseFloat(getValue('weight_profitability', '0.25')),
      weight_driver_score: parseFloat(getValue('weight_driver_score', '0.20')),
      min_score_threshold: parseFloat(getValue('min_score_threshold', '0.3')),
      distance_max_miles: parseFloat(getValue('distance_max_miles', '50')),
      return_max_miles: parseFloat(getValue('return_max_miles', '50')),
      max_dispatches: parseInt(getValue('max_dispatches', '5'), 10),
      default_start_time: getValue('default_start_time', '06:00'),
      default_drop_duration: parseInt(getValue('default_drop_duration', '45'), 10),
      default_live_duration: parseInt(getValue('default_live_duration', '90'), 10),
      default_projected_pay: parseFloat(getValue('default_projected_pay', '50.00')),
      round_trip_live_count: parseInt(getValue('round_trip_live_count', '1'), 10),
      round_trip_drop_count: parseInt(getValue('round_trip_drop_count', '2'), 10),
    };
  },

  /**
   * Analyze a pre-plan using AI and get suggestions
   */
  analyzePrePlan: async (plan: {
    plan_id: string;
    target_date: string;
    region_name: string;
    config_used?: Record<string, unknown>;
    statistics: Record<string, unknown>;
    driver_plans: Record<string, unknown>[];
    unassigned_moves: Record<string, unknown>[];
    available_drivers?: Record<string, unknown>[];
    model?: string; // AI model to use: 'sonnet', 'opus', or 'haiku'
  }): Promise<{
    analysis: string;
    suggestions: string[];
    warnings: string[];
    optimization_score?: number;
    input_tokens?: number;
    output_tokens?: number;
    model_used?: string;
  }> => {
    const response = await api.post('/analyze-preplan', plan);
    return response.data;
  },

  /**
   * Get AI-powered optimization suggestions for a pre-plan
   * Returns specific, actionable suggestions for assignments and swaps
   */
  getAIOptimizations: async (plan: {
    plan_id: string;
    target_date: string;
    region_name: string;
    config_used?: Record<string, unknown>;
    statistics: Record<string, unknown>;
    driver_plans: Record<string, unknown>[];
    unassigned_moves: Record<string, unknown>[];
    available_drivers?: Record<string, unknown>[];
    model?: string;
  }): Promise<{
    suggestions: Array<{
      type: 'assign' | 'swap' | 'remove' | 'warning';
      priority: 'high' | 'medium' | 'low';
      description: string;
      move_pro?: string;
      move_legnum?: number;
      suggested_driver?: string;
      suggested_driver_name?: string;
      from_driver?: string;
      from_driver_name?: string;
      to_driver?: string;
      to_driver_name?: string;
      reasoning?: string;
    }>;
    summary: string;
    input_tokens?: number;
    output_tokens?: number;
    model_used?: string;
  }> => {
    const response = await api.post('/ai-optimize', plan);
    return response.data;
  },

  /**
   * Validate driver schedules after drag-and-drop reassignment.
   * Checks if moves can be arranged without missing appointments and returns updated timing.
   */
  validateSchedules: async (request: {
    target_date: string;
    region_name: string;
    driver_assignments: Array<{
      unitnumber: string;
      moves: Array<{
        pronumber: number;
        legnum: number;
        sequence: number;
      }>;
    }>;
    buffer_minutes?: number;
  }): Promise<{
    valid: boolean;
    driver_results: Array<{
      unitnumber: string;
      valid: boolean;
      error?: string;
      conflict_move_index?: number;
      moves: Array<{
        pronumber: number;
        legnum: number;
        sequence: number;
        estimated_start: string | null;
        estimated_end: string | null;
        travel_time_from_previous: number;
        arrival_time: string | null;
      }>;
    }>;
    error?: string;
  }> => {
    const response = await api.post('/smart-dispatch/validate-schedules', request);
    return response.data;
  },

  /**
   * Apply pre-planned driver assignments to CMS.
   * Processes all moves in batch, collecting results for each.
   * Also updates preplan tables (assignments marked as applied, unassigned resolved).
   */
  applyPreplan: async (
    moves: Array<{
      pronumber: number;
      legnum: number;
      driver: string;
      origcode?: number;
      destcode?: number;
      priority?: number;
      // Sent for EVERY proposed street turn, ticked or not: it is what identifies the
      // move as a reuse, and identifying it is what keeps it out of the dispatch batch.
      // Its planned destination is the export's shipper, so dispatching it without
      // performing the reuse would send a driver there with the import still expecting
      // its empty back.
      street_turn_export?: number;
      street_turn_export_legnum?: number;
      // The opt-in, and the only thing that authorises the write.
      perform_street_turn?: boolean;
    }>,
    targetDate?: string,
    planId?: string
  ): Promise<{
    total: number;
    succeeded: number;
    failed: number;
    results: Array<{
      pronumber: number;
      legnum: number;
      driver: string;
      success: boolean;
      error?: string;
      // Held back by a hard stop (an open rejection ticket) before anything wrote.
      blocked?: boolean;
      street_turn_export?: number;
    }>;
    // Present when the plan carried any proposed turn. The e-mails to the steamship
    // lines are still going out when this returns — nobody waits on them, because the
    // user applying is not copied.
    street_turns?: {
      performed: number;
      failed: number;
      not_selected: number;
      email_batch: number | null;
    } | null;
    cms_response?: unknown;  // Raw CMS response for debugging
  }> => {
    const response = await api.post('/smart-dispatch/apply-preplan', {
      plan_id: planId,
      moves,
      target_date: targetDate,
    });
    return response.data;
  },

  /**
   * Mark assignments as unapplied after removing driver from moves.
   * This updates the preplan_assignments table to reflect that the move
   * was subsequently unassigned.
   *
   * Returns moves_to_clear_appointment: moves that had no original appointment date
   * and thus need their appointment cleared in CMS.
   */
  markAssignmentsUnapplied: async (
    planId: string,
    // The row's id when the caller has it, as undo does: a street turn's row is the
    // import move the board planned, not the export leg undo cleared.
    moves: Array<{ pronumber: number; legnum: number; assignment_id?: number }>
  ): Promise<{
    success: boolean;
    updated: number;
    moves_to_clear_appointment?: Array<{ pronumber: number; legnum: number }>;
  }> => {
    const response = await api.post('/smart-dispatch/mark-assignments-unapplied', {
      plan_id: planId,
      moves,
    });
    return response.data;
  },

  /**
   * Get applied preplans for a date/region.
   * Returns plans with counts of currently-applied assignments.
   */
  getAppliedPlans: async (
    targetDate: string,
    regionName: string
  ): Promise<{
    plans: Array<{
      plan_id: string;
      applied_at: string | null;
      applied_by: string | null;
      created_at: string | null;
      status: string;
      region_name: string;
      applied_count: number;
      total_assignments: number;
    }>;
    count: number;
  }> => {
    const params = new URLSearchParams();
    params.append('target_date', targetDate);
    params.append('region_name', regionName);
    const response = await api.get(`/smart-dispatch/applied-plans?${params.toString()}`);
    return response.data;
  },

  /**
   * Get applied moves for a specific plan.
   * Returns move details needed to undo them: removeFromDriver, or handing a reassigned
   * leg back to its original driver, or reversing a street turn.
   */
  getAppliedPlanMoves: async (
    planId: string
  ): Promise<{
    plan_id: string;
    moves: Array<{
      assignment_id: number;
      pronumber: number;
      legnum: number;
      driver: string;
      // Who held the leg before the apply: '' for nobody, null when the row was applied
      // before that was recorded. Undo hands a reassigned leg back to them.
      original_driver: string | null;
      // Set when the apply performed a street turn: the import whose container went
      // onto `pronumber`, the export. Undo reverses the reuse.
      street_turn_import: number | null;
      dest_appt_date: string | null;
      dispatch_id: number | null;
      clear_appointment: boolean;
      origcode: number;
      destcode: number;
      orig_appt_date: string | null;
      // Location details for staging
      origin_name: string;
      origin_city: string;
      dest_name: string;
      dest_city: string;
      dest_appt_time1: string;
      dest_appt_time2: string;
      // Dispatch group for linked legs
      dispatch_group_id: string | null;
    }>;
    count: number;
    stale_cleaned?: number;  // Moves that were already removed outside our system
  }> => {
    const response = await api.get(`/smart-dispatch/applied-plan-moves/${planId}`);
    return response.data;
  },
};

export default preplanningService;
